import React, { useState } from "react";
import Language from "../lib/Language";
import ReservationInfo from "./ReservationInfo";
import { Reservation } from "../models/Reservation";
import { Macros } from "../models/Macros";
import { useAppContext } from "../contexts/AppContext";
import { Entry } from "../models/Entry";
import SidebarDismissButton from "./SidebarDismissButton";

type TReservationPaneProps = {
    reservationIds: number[];
    isEntryInfo: boolean;
    onClose: () => void;
    onObjectInfo: (
        object: any,
        createCopy: boolean | undefined,
        viewOnly: boolean | undefined,
        title: string
    ) => void;
    // Not read here: spread through to ReservationInfo, which uses both
    // (getUser, and layer filtering via isOnDifferentLayer).
    user: any;
    activeLayer: number;
    // Omitted by the "dynamic" call site in Menu.
    dynamic?: boolean;
    isInfoEntryEdited?: boolean;
    onReservationEditChange?: (isEditing: boolean) => void;
    onEntryInfoOpen?: (
        entry: Entry | Entry[] | number[] | null,
        showPanel: boolean,
        editMode: boolean
    ) => void;
};

const ReservationPane = (props: TReservationPaneProps) => {
    const appContext = useAppContext();
    const [title, setTitle] = useState("");

    const getTitle = () => {
        if (title) {
            return title;
        }

        const reservationIds = props.reservationIds;
        if (reservationIds.length === 1) {
            return `${Language.get("cal_func_res_reservation")} #${reservationIds[0]}`;
        }

        if (reservationIds.length === 0 || !props.isEntryInfo) {
            return Language.get("cal_res_side_tab_res_info");
        }

        return Language.get("period_prefs_cluster");
    };

    const onDelete = () => {
        const reservationId = props.reservationIds;

        const removeEntry = (reservationId) => {
            Reservation.cancel(reservationId, (wasSuccessful) => {
                if (!wasSuccessful) {
                    return;
                }

                props.onEntryInfoOpen?.(null, false, false);
                appContext.fireEvent(
                    "menu",
                    Macros.Event.RESERVATION_MADE_OR_MODIFIED,
                    [],
                    false,
                    false
                );
            });
        };

        const onRemember = () => {
            const currentUser = appContext.user;
            if (!currentUser) {
                return;
            }
            const user = currentUser.immutableSet({
                showConfirmEntryRemovalWarning: false,
            });
            appContext.update(appContext.user, user);
        };

        Reservation.get(reservationId, ([reservation]) => {
            if (!reservation) {
                return;
            }
            const userId = appContext.user?.userId;
            if (userId != null && reservation.createdby === userId) {
                removeEntry(reservationId);
            } else {
                if (appContext.user?.showConfirmEntryRemovalWarning) {
                    const yesButton = {
                        title: Language.get("dialog_yes"),
                        cb: () => {
                            removeEntry(reservationId);
                        },
                        remember: true,
                        value: false,
                    };
                    const noButton = { title: Language.get("dialog_no"), remember: false };
                    const buttons = [yesButton, noButton];
                    appContext.presentModal(
                        <div>{<p>{Language.get("nc_confirm_entry_removal_message")}</p>}</div>,
                        "confirm_entry_removal",
                        Language.get("nc_confirm_entry_removal_title"),
                        buttons,
                        onRemember
                    );
                } else {
                    removeEntry(reservationId);
                }
            }
        });
    };

    const onUpdate = (ids = props.reservationIds || []) => {
        if (ids) {
            appContext.fireEvent(
                "menu",
                Macros.Event.RESERVATION_MADE_OR_MODIFIED,
                ids,
                false,
                false
            );
        }
    };

    const classNames = ["reservationPane"];
    if (props.dynamic === true) {
        classNames.push("fixToBottom");
    }
    const validIds = props.reservationIds.filter((id) => id !== null && id !== undefined && id > 0);
    if (validIds.length === 0) {
        return null;
    }

    const content =
        validIds.length > 0 ? (
            <ReservationInfo
                {...props}
                onDelete={onDelete}
                onUpdate={onUpdate}
                reservationIds={props.reservationIds}
                dynamic={props.dynamic}
                isEditMode={props.isInfoEntryEdited}
                onReservationEditChange={props.onReservationEditChange}
                setTitle={setTitle}
                onObjectInfo={props.onObjectInfo}
                onEntryInfoOpen={props.onEntryInfoOpen}
            />
        ) : (
            <p>{Language.get("nc_menu_reservation_not_selected")}</p>
        );

    return (
        <div className={classNames.join(" ")}>
            <div style={{ display: "block" }}>
                <SidebarDismissButton useNewUI={false} onClose={props.onClose} />
                <h2>{getTitle()}</h2>
            </div>
            {content}
        </div>
    );
};

export default ReservationPane;
